/* eslint-disable react/jsx-no-useless-fragment */
import React, { forwardRef } from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'

import Icon from './Icon'
import Spinner from './Spinner'
import { colorPrimary } from '../../styles/theme'

const InversedButton = forwardRef(
    (
        {
            iconName,
            timesheetCount,
            iconPosition = 'left',
            children,
            isSmall,
            isLoading,
            isAnimated = true,
            isAddonBefore,
            isAddonAfter,
            hasAddonBefore,
            hasAddonAfter,
            ...props
        },
        ref,
    ) => {
        const iconIsSet = typeof iconName !== 'undefined'
        const isIconButton = typeof children === 'undefined' && iconIsSet
        return (
            <Button
                ref={ref}
                whileTap={{ scale: isAnimated ? 1 : 0.95 }}
                $isAddonBefore={isAddonBefore}
                $isAddonAfter={isAddonAfter}
                $hasAddonBefore={hasAddonBefore}
                $hasAddonAfter={hasAddonAfter}
                $isSmall={isSmall}
                {...props}
            >
                {iconIsSet && !isLoading && iconPosition === 'left' && (
                    <StyledIconLeft
                        isSmaller
                        name={iconName}
                        $isIconButton={isIconButton}
                    />
                )}
                {timesheetCount > 0 && 
                    <StyledBadge>
                        +{timesheetCount}
                    </StyledBadge>
                }
                {isLoading && (
                    <SpinnerContainer>
                        <Spinner size={12} color={colorPrimary} />
                    </SpinnerContainer>
                )}
                <div>{children}</div>
                {iconIsSet && iconPosition === 'right' && (
                    <StyledIconRight
                        isSmaller
                        name={iconName}
                        $isIconButton={isIconButton}
                    />
                )}
            </Button>
        )
    },
)

const Button = styled(motion.button)(
    ({
        theme,
        $isAddonBefore,
        $isAddonAfter,
        $hasAddonBefore,
        $hasAddonAfter,
        disabled,
        $isSmall,
    }) => `
    display: flex;
    background-color: ${theme.colorWhite};
    border-color: ${theme.colorPrimary};
    border-top-width : ${theme.defaultBorderWidth}rem;
    border-bottom-width : ${theme.defaultBorderWidth}rem;
    border-left-width : ${$isAddonAfter ? 0 : theme.defaultBorderWidth}rem;
    border-right-width : ${$isAddonBefore ? 0 : theme.defaultBorderWidth}rem;
    border-top-left-radius: ${
        $hasAddonBefore || $isAddonAfter ? 0 : theme.defaultButtonBorderRadius
    }rem;
    border-bottom-left-radius: ${
        $hasAddonBefore || $isAddonAfter ? 0 : theme.defaultButtonBorderRadius
    }rem;
    border-top-right-radius: ${
        $hasAddonAfter || $isAddonBefore ? 0 : theme.defaultButtonBorderRadius
    }rem;
    border-bottom-right-radius: ${
        $hasAddonAfter || $isAddonBefore ? 0 : theme.defaultButtonBorderRadius
    }rem;
    color: ${theme.colorPrimary};
    padding: ${
        $isSmall
            ? `${theme.spacingSmallest}rem ${theme.spacingSmaller}rem`
            : `${theme.spacingSmaller}rem ${theme.spacingSmall}rem`
    };
    font-size: ${theme.fontSize}rem;
    font-weight: ${$isSmall ? theme.fontWeightMedium : theme.fontWeightBold};
    justify-content: center;
    align-items: center;
    :hover {
        background-color: ${!disabled && theme.colorPrimaryLightest};
    }
    :disabled {
        cursor: not-allowed;
        color: ${theme.colorGreyLight};
        border-color: ${theme.colorGreyLight};
    }
`,
)

const StyledIconLeft = styled(Icon)(
    ({ theme, $isIconButton }) => `
    margin-right: ${!$isIconButton && theme.spacingSmaller}rem;
`,
)

const StyledIconRight = styled(Icon)(
    ({ theme, $isIconButton }) => `
    margin-left: ${!$isIconButton && theme.spacingSmall}rem;
`,
)

const SpinnerContainer = styled.span(
    ({ theme }) => `
    font-size: ${theme.fontSizeSmall}rem;
    height: 12px;
    margin-right: ${theme.spacingSmaller}rem;
`,
)

const StyledBadge = styled.div(
    ({ theme }) => `
  margin-right: ${theme.spacingSmaller}rem;
  background-color: ${theme.colorPrimary};
  color: ${theme.colorWhite};
  font-size: ${theme.fontSizeSmall}rem;
  font-weight: ${theme.fontWeightRegular}rem;
  padding: ${theme.spacingSmallest}rem ${theme.spacingSmaller}rem;
  border-radius: ${theme.spacingSmallest}rem;;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${theme.fontSizeLarge}rem;
  height: ${theme.fontSizeLarge}rem;
`,
)

export default InversedButton
